import { AxiosResponse } from 'axios';
import {
  CandidateTechStacksType,
  Auth0User,
  TechStackType,
  ResponseWithPagination,
  AvailableRoles,
} from '@/types';
import { InterviewTypes } from '@/utils/interviews';
import { GradingRubric } from './gradingRubric';
import { TechnicalChallengeStatus } from './interviewTechnicalChallenge';
import { PTOPolicy } from './pto';
import {
  TechnicalChallengeCompetency,
  TechnicalChallengeCompetencyScore,
} from './technicalChallenge';

export * from './pto';

export type Permission = {
  id?: string;
  value: string;
  createdAt?: Date;
};

export type Role = {
  id?: string;
  value: AvailableRoles;
  permissions: Permission[];
  createdAt?: Date;
};

export type User = {
  auth0Id: string | null;
  createdAt: string;
  deletedAt: string | null;
  email: string;
  id: string;
  name: string;
  personId: string | null;
  person: PersonType | null;
  roles: Role[];
  slackId: string | null;
  updatedAt: string;
  isOwner?: boolean;
};

export type CreatedUser = Omit<User, 'person'>;

export type InviteUserResponse = {
  user: CreatedUser;
};

export type UsersCheckinsSummary = {
  userId: string;
  userName: string;
  avgSubmissionRate: number;
  checkinsSubmitted: number;
  checkinsToBeDone?: number;
  existingCheckins?: number;
};

export type InviteUser = {
  name?: string;
  email: string;
  roles: AvailableRoles[];
  usePassword?: boolean;
  password?: string;
};

export type EditUserOptions = {
  id: string;
  name?: string;
  email?: string;
  roles?: AvailableRoles[];
};

export type ResponseApiUsersCheckinsSummary = {
  summary: UsersCheckinsSummary[];
};

export type TCheckinHistory = {
  endDate: Date | string;
  isPending: boolean;
  submittedDate: Date | string | undefined;
  checkinId: string;
};

export type DueDate = {
  createdAt: Date | string;
  endDate: Date | string;
  id: string;
  questions: string[];
  startDate: Date | string;
  updatedAt: Date | string;
};

export type UserCheckin = {
  answers: Answer[];
  feedbacks: Feedback[];
  questions: Question[];
  dueDate: DueDate;
  user: User;
  createdAt: Date | string;
  dueDateId: string;
  id: string;
  isReviewed: boolean;
  reviewerId: string;
  updatedAt: string | Date;
  answeredAt?: Date | string;
  reviewedAt?: string | Date;
  reviewer?: User;
  prioritiesFromLastWeek?: Answer[];
};

export type CheckinSummary = {
  answeredAt?: string;
  dueDate: string;
  id: string;
  isReviewed: boolean;
  userName: string;
  userId: string;
};

export type Answers = {
  description: string;
  questionId: string;
  surveyId: string;
};

export type AnswerCheckinDetails = {
  answersId: string;
  questionId: string;
  checkinId: string;
  response: string;
};

export type QuestionAnswersCheckinDetails = {
  questionId: string;
  label: string;
  answerType: AnswerTypes;
  questionDate: string;
  answers: AnswerCheckinDetails[];
};

export type UserCheckinAnswers = {
  userId: string;
  userName: string;
  userEmail: string;
  checkinDate: Date;
  questions: QuestionAnswersCheckinDetails[];
};

export type ResponseApiUserCheckinAnswers = {
  userCheckinAnswers: UserCheckinAnswers;
};

export type Answer = {
  id: string;
  questionId: string;
  response: string;
  submittedQuestionLabel: string;
  checkinId: string;
  created_at: string | Date;
  updated_at: string | Date;
  isFinished?: boolean;
  question: Question;
  comments: Comment[];
  reactions?: TReaction[];
};

export type UsersCheckins = {
  name: string;
  date: Date;
};

export type ResponseApiUsersCheckins = {
  usersCheckins: UsersCheckins[];
};

export type ResponseApiProfile = {
  profile: Auth0User;
};

export enum AnswerTypes {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  ARRAY = 'ARRAY',
  PRIORITY = 'PRIORITY',
}

export type QuestionOption = {
  id: string;
  label: string;
  questionId: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export type Question = {
  id: string;
  label: string;
  answerType: AnswerTypes;
  isRequired: boolean;
  questionFrequency: QuestionFrequency;
  description?: string;
  isActive?: boolean;
  options?: QuestionOption[];
  scheduledAt?: Date | string | null;
  createdAt?: Date;
  updatedAt?: Date;
};

export type RotatingQuestionInputData = Omit<Question, 'id' | 'createdAt' | 'updatedAt'> & {
  id?: string;
};

export const questionOptionsRange = [
  {
    value: 1,
    label: '1',
  },
  {
    value: 2,
    label: '2',
  },
  {
    value: 3,
    label: '3',
  },
  {
    value: 4,
    label: '4',
  },
  {
    value: 5,
    label: '5',
  },
  {
    value: 6,
    label: '6',
  },
  {
    value: 7,
    label: '7',
  },
  {
    value: 8,
    label: '8',
  },
  {
    value: 9,
    label: '9',
  },
  {
    value: 10,
    label: '10',
  },
];

export const questionTypes = [
  {
    key: AnswerTypes.STRING,
    value: AnswerTypes.STRING,
    text: 'Text',
  },
  {
    key: AnswerTypes.ARRAY,
    value: AnswerTypes.ARRAY,
    text: 'Multiple texts',
  },
  {
    key: AnswerTypes.NUMBER,
    value: AnswerTypes.NUMBER,
    text: 'Number',
  },
];

export type ResponseApiUser = {
  user: User;
};

export type ResponseApiAllUsers = {
  users: ResponseWithPagination<User[]>;
};

export type Comment = {
  id: string;
  text: string;
  answerId: string;
  userId: string;
  created_at: Date;
  updated_at: Date;
  user: User;
  reactions?: TReaction[];
};

export type Feedback = {
  id: string;
  text: string;
  checkinId: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  user?: User;
  reactions?: TReaction[];
};

export enum QuestionFrequency {
  SHOW_EVERY_CHECKIN = 'SHOW_EVERY_CHECKIN',
  ONE_TIME_QUESTION = 'ONE_TIME_QUESTION',
  QUESTION_QUEUED = 'QUESTION_QUEUED',
  SCHEDULED = 'SCHEDULED',
}

export type QuestionFrequencyDataType = {
  title: string;
  key: QuestionFrequency;
  value: QuestionFrequency;
};

export const QuestionFrequencyData: {
  [key in QuestionFrequency]: QuestionFrequencyDataType;
} = {
  SHOW_EVERY_CHECKIN: {
    title: 'Show Every Check-in',
    key: QuestionFrequency.SHOW_EVERY_CHECKIN,
    value: QuestionFrequency.SHOW_EVERY_CHECKIN,
  },
  ONE_TIME_QUESTION: {
    title: 'One-time Question',
    key: QuestionFrequency.ONE_TIME_QUESTION,
    value: QuestionFrequency.ONE_TIME_QUESTION,
  },
  QUESTION_QUEUED: {
    title: 'Question Queued',
    key: QuestionFrequency.QUESTION_QUEUED,
    value: QuestionFrequency.QUESTION_QUEUED,
  },
  SCHEDULED: {
    title: 'Scheduled Question',
    key: QuestionFrequency.SCHEDULED,
    value: QuestionFrequency.SCHEDULED,
  },
};

export interface AnswersOptions {
  label: number;
  count: number;
}

export interface UsersAnswers {
  name: string;
  label: string;
  answerDate: string;
}

export type AnswersWeek = {
  startDate: string;
  endDate: string;
  answerAvg: number;
  usersAnswers?: UsersAnswers[] | null;
  answersOptions?: AnswersOptions[] | null;
};

export type Trend = {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  answerAvg: number;
  options?: QuestionOption[] | null;
  answersWeek?: AnswersWeek[] | null;
};

export type ResponseGetTrends = {
  trends: Trend[];
};

export type ResponseGetTrendDetail = {
  trend: Trend;
};

type PulseWeeks = {
  startDate: Date | string;
  endDate: Date | string;
  avgPulse: number;
  totalSubmittedPulse: number;
};

export type PulseReport = {
  startDate: Date | string;
  endDate: Date | string;
  avgPulse: number;
  pulsesWeek: PulseWeeks[];
};

export type ResponseGetPulseReport = {
  pulse: PulseReport;
};

type CheckinsReportWeek = {
  startDate: Date | string;
  endDate: Date | string;
  checkinAvg: number;
};

export type CheckinsReport = {
  startDate: Date | string;
  endDate: Date | string;
  avgSubmissionRate: number;
  differenceAvgSubmissionRate: number;
  submittedCheckins: number;
  submittedOnTime: number;
  checkinsWeek: CheckinsReportWeek[];
};

export type ResponseGetCheckinsReport = {
  submittedCheckIns: CheckinsReport;
};

export type TeamMember = {
  createdAt: string;
  id: string;
  teamId: string;
  updatedAt: string;
  user: User | null;
  userId: string;
};

export type TeamCategory = 'Internal' | 'External';
export type TeamArea =
  | 'People X'
  | 'Engineering X'
  | 'Partner X'
  | 'Sales'
  | 'Talent X'
  | 'Marketing'
  | 'Finance';

export type Team = {
  id: string;
  name: string;
  createdAt: string;
  ptoPolicy?: PTOPolicy;
  members: TeamMember[];
  area: TeamArea | null;
  category: TeamCategory | null;
  manager: PersonType | null;
  managerId: string | null;
  pictureUrl: string | null;
  ptoPolicyId: string | null;
  updatedAt: string;
};

export type TeamsApiResponse = {
  teams: ResponseWithPagination<Team[]>;
};

export type TReaction = {
  answer?: string;
  answerId?: string;
  comment: Comment;
  user: User;
  commentId?: string;
  createdAt: string;
  emoji: string;
  feedback?: string;
  feedbackId?: string;
  id: string;
  updatedAt: string;
  userId: string;
};

export type ListOfCheckinsByTeam = {
  checkins: UserCheckin[];
  name: string;
  pendingCheckins: number;
  teamId: string;
};

export type ListOfCheckinsByTeamResponse = {
  teams: ListOfCheckinsByTeam[];
};

export enum FileCategories {
  AGREEMENTS = 'AGREEMENTS',
  CONTRACT = 'CONTRACT',
  CONTRACTS = 'CONTRACTS',
  COVER_LETTER = 'COVER LETTER',
  DOCUMENTS = 'DOCUMENTS',
  FORMATTED_RESUME = 'FORMATTED RESUME',
  OFFER = 'OFFER',
  OTHER = 'OTHER',
  RESUME = 'RESUME',
}

export type CandidateAttachment = {
  id: string;
  name: string;
  attachedBy: string;
  size: string;
  url: string;
  category: string;
  candidateId: string;
  createdAt: string;
  updatedAt: string;
  user: User;
};

export type UploadedCandidateAttachments = Partial<{
  [category in FileCategories]: CandidateAttachment[];
}>;

export enum NotificationGroups {
  everyone = 'everyone',
  team = 'team',
}

type NotificationDetails = {
  group: NotificationGroups;
  teamId?: string;
};

export type NotificationParams = {
  type: string;
  data: NotificationDetails;
};

export type CandidateNoteAuthorType = {
  name: string;
  auth0Id: string;
  createdAt: string;
  deletedAt: string;
  email: string;
  id: string;
  slackId: string;
};

export type TopicNoteType = {
  id: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  author: Partial<CandidateNoteAuthorType>;
};

export type TopicType = {
  createdAt: string;
  id: string;
  isDefault: boolean;
  name: string;
  notes: TopicNoteType[];
  stageId: string;
  updatedAt: string;
};

export type CandidateTopicType = TopicType & {
  candidateId: string;
};

export type CandidateStageType = {
  createdAt: string;
  id: string;
  name: string;
  topics: CandidateTopicType[];
  updatedAt: string;
};

export type CandidateStagesResponse = {
  stages: CandidateStageType[];
};

export type TotalCandidatesByTechStack = {
  totalCandidates: number;
  name: string;
};

export type AvgTimeToAcceptanceOrRejectionByOwnerChart = {
  partners: number;
  trio: number;
};

export type AvgTimeToAcceptanceOrRejectionByOwnerResponse = {
  owner: string;
  chart: AvgTimeToAcceptanceOrRejectionByOwnerChart;
};

export type AvgTimeByTechStackChartData = {
  trio: number;
  partners: number;
  trioPercentage: number;
  partnersPercentage: number;
};

export type AvgTimeByTechStackData = {
  tech: string;
  chart: AvgTimeByTechStackChartData;
};

type PersonSkillType = {
  createdAt: string;
  description?: string | null;
  experience: string;
  experienceRange: string;
  id: string;
  isMainStack: boolean;
  willingToReview: boolean;
  tech: TechStackType;
};

export type PersonGender = 'Male' | 'Female' | 'Non-binary' | 'Don’t want to declare';
export type Ethnicity =
  | 'African American'
  | 'African/Black'
  | 'American Indian, Alaska Native, or other Indigenous'
  | 'Caucasian/White'
  | 'East Asian'
  | 'Hispanic and/or Latin American'
  | 'Middle Eastern/Arab'
  | 'Mixed Race/Mestizo'
  | 'Native Hawaiian or other Pacific Islander'
  | 'Other ethnic group'
  | 'South Asian';

export type EmployeeWorkStatus = 'Active' | 'Inactive';
export type EmergencyContactRelationship =
  | 'Brother'
  | 'Daughter'
  | 'Father'
  | 'Friend'
  | 'Husband'
  | 'Mother'
  | 'Other'
  | 'Partner'
  | 'Sister'
  | 'Son'
  | 'Wife';

export type PersonType = {
  addressLine1: string | null;
  addressLine2: string | null;
  availability: string | null;
  birthdate: string | null;
  candidateOwner: null | string;
  city: string;
  communicationSkillsRating: number;
  country: string | null;
  createdAt: string;
  deletedAt: null | string;
  developerType: string | null;
  email?: string | null;
  emergencyContactEmail: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: EmergencyContactRelationship | null;
  employeeWorkStatus: EmployeeWorkStatus | null;
  englishLevel: null | string;
  ethnicity: Ethnicity | null;
  experience: string;
  firstName: string;
  gender: PersonGender | null;
  githubUrl: string | null;
  id: string;
  shortId: string;
  isTalentPool: boolean;
  lastName: string;
  linkedinUrl: string;
  mainTechStack?: PersonSkillType[];
  mobilePhone: string;
  owner: null | string;
  ownerId: string;
  pictureUrl?: string | null;
  rating: number;
  referredBy: string;
  salaryExpectation: string;
  skills: CandidateTechStacksType[] | string[];
  softSkillsRating: number;
  source: string;
  state: string | null;
  status: string | null;
  summary: string | null;
  technicalSkillsRating: number;
  trioProfileUrl: string | null;
  updatedAt: string;
  workEmail: string | null;
  zipCode: string | null;
  hasFintechExperience: boolean;
};

export type InterviewAnswerType = {
  createdAt: string;
  id: string;
  updatedAt: string;
  attachmentUrl?: string;
  textAnswer?: string;
};

export type InterviewQuestionType = {
  createdAt: string;
  id: string;
  questionText: string;
  updatedAt: string;
  answer?: InterviewAnswerType;
};

export type CompetencyScore = {
  id?: string;
  name: string;
  label: string;
  value: string; // Stringified value to be used in the Inputs
  scoreValue?: number; // Numberic value to be used in the score calculation
  maxScoreValue?: number;
  options: string[];
  technicalChallengeCompetencyScoreId?: string;
  technicalChallengeCompetencyScore?: TechnicalChallengeCompetencyScore;
};

export type TechnicalValidationCompetency = {
  name: string;
  additionalInfo?: string;
  scores: CompetencyScore[];
  weight: number;
  competency?: TechnicalChallengeCompetency;
};

export type InterviewReviewType = {
  id: string;
  createdAt: string;
  updatedAt: string;
  note?: string;
  aiGeneratedOverview?: string | null;
  user: User;
  score: InterviewScore[];
  competencies?: TechnicalValidationCompetency[];
};

export type InterviewNoteType = {
  id: string;
  createdAt: string;
  updatedAt: string;
  note?: string;
  user: User;
};

export type TechnicalChallenge = {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  description?: string;
  url: string;
  isActive: boolean;
  gradingRubric?: GradingRubric;
};

export type TechnicalChallengeSuggestions = {
  id: string;
  person: PersonType;
  skills: CandidateTechStacksType[];
};

type InterviewReviewerType = {
  id: string;
  person: PersonType;
};

export type TechnicalChallengeInterviewReviewers = {
  id: string;
  interviewReviewer: InterviewReviewerType;
  status: string;
  interviewTechnicalChallenge: InterviewTechnicalChallenge;
};

export type InterviewTechnicalChallenge = {
  id: string;
  interview: InterviewType;
  technicalChallenge: TechnicalChallenge;
  gitlabProjectUrl?: string;
  githubProjectUrl?: string;
  technicalVideoUrl?: string;
  technicalDocumentTitle?: string;
  technicalDocumentContent?: string;
  status?: TechnicalChallengeStatus;
  createdAt: string;
  updatedAt: string;
  reviewers: TechnicalChallengeInterviewReviewers[];
  languageUsedByCandidate?: string; // Deprecated: kept for backward compatibility
  skillId?: string;
  skill?: TechStackType;
};

export interface PaginatedResponse<T> {
  data: Array<T>;
  count: number;
  currentPage: number;
  nextPage: number | null;
  prevPage: number | null;
  lastPage: number;
}

export type InterviewType = {
  createdAt: string;
  finalScore?: string;
  id: string;
  isAlreadySent: boolean;
  notes: InterviewNoteType[];
  person: PersonType;
  questions: InterviewQuestionType[];
  recordingUrl: string | null;
  reviews: InterviewReviewType[];
  reviewsResult?: string;
  technicalChallenge: InterviewTechnicalChallenge | null;
  title: string;
  type: InterviewTypes;
  updatedAt: string;
  user: User;
};

export enum InterviewScoreEnum {
  // Pre-Screening scores
  FLUENCY = 'FLUENCY',
  VOCABULARY = 'VOCABULARY',
  SPONTANEITY = 'SPONTANEITY',
  // Screening scores
  SELF_MANAGEMENT = 'SELF_MANAGEMENT',
  EXCELLENCE = 'EXCELLENCE',
  CULTURAL_FIT = 'CULTURAL_FIT',
  YEARS_OF_EXPERIENCE_WITH_REQUESTED_TECHNOLOGY = 'YEARS_OF_EXPERIENCE_WITH_REQUESTED_TECHNOLOGY',
  ENGLISH = 'ENGLISH',
  // Screening and Team Interview scores
  COMMUNICATION = 'COMMUNICATION',
  COLLABORATION = 'COLLABORATION',
  // Team Interview scores
  AWARENESS = 'AWARENESS',
  CURIOSITY = 'CURIOSITY',
  TECHNICAL_KNOWLEDGE_AND_PROBLEM_SOLVING = 'TECHNICAL_KNOWLEDGE_AND_PROBLEM_SOLVING',
  // Technical Validation scores
  TECHNICAL_VALIDATION_CONTAINS_ERROR = 'TECHNICAL_VALIDATION_CONTAINS_ERROR',
  TECHNICAL_VALIDATION_FOLLOWS_BEST_PRACTICES = 'TECHNICAL_VALIDATION_FOLLOWS_BEST_PRACTICES',
  TECHNICAL_VALIDATION_FOLLOWS_CURRENT_STRUCTURE = 'TECHNICAL_VALIDATION_FOLLOWS_CURRENT_STRUCTURE',
  TECHNICAL_VALIDATION_MEANINGFUL_NAMES = 'TECHNICAL_VALIDATION_MEANINGFUL_NAMES',
  TECHNICAL_VALIDATION_NAMED_CONDITIONS = 'TECHNICAL_VALIDATION_NAMED_CONDITIONS',
  TECHNICAL_VALIDATION_BOOLEAN_SUFFIXES = 'TECHNICAL_VALIDATION_BOOLEAN_SUFFIXES',
  TECHNICAL_VALIDATION_SMALL_FUNCTIONS = 'TECHNICAL_VALIDATION_SMALL_FUNCTIONS',
  TECHNICAL_VALIDATION_ONE_LEVEL_INDENTATION = 'TECHNICAL_VALIDATION_ONE_LEVEL_INDENTATION',
  TECHNICAL_VALIDATION_CONSISTENT_CODEBASE = 'TECHNICAL_VALIDATION_CONSISTENT_CODEBASE',
  TECHNICAL_VALIDATION_WELL_SEPARATED_CODE = 'TECHNICAL_VALIDATION_WELL_SEPARATED_CODE',
  TECHNICAL_VALIDATION_NOT_CONTAIN_REPETITION = 'TECHNICAL_VALIDATION_NOT_CONTAIN_REPETITION',
  TECHNICAL_VALIDATION_SAME_CODE_STYLE = 'TECHNICAL_VALIDATION_SAME_CODE_STYLE',
  TECHNICAL_VALIDATION_CONTAINS_UNIT_TESTS = 'TECHNICAL_VALIDATION_CONTAINS_UNIT_TESTS',
  TECHNICAL_VALIDATION_CONTAINS_INTEGRATION_TESTS = 'TECHNICAL_VALIDATION_CONTAINS_INTEGRATION_TESTS',
  TECHNICAL_VALIDATION_COVERAGE_FIFTY_SEVENTY = 'TECHNICAL_VALIDATION_COVERAGE_FIFTY_SEVENTY',
  TECHNICAL_VALIDATION_COVERAGE_SEVENTY_ONE_HUNDRED = 'TECHNICAL_VALIDATION_COVERAGE_SEVENTY_ONE_HUNDRED',
  TECHNICAL_VALIDATION_TECHNICAL_DESIGN_QUALITY = 'TECHNICAL_VALIDATION_TECHNICAL_DESIGN_QUALITY',
  TECHNICAL_VALIDATION_WELL_SEPARATED_COMMITS = 'TECHNICAL_VALIDATION_WELL_SEPARATED_COMMITS',
  TECHNICAL_VALIDATION_CLEAR_COMMITS = 'TECHNICAL_VALIDATION_CLEAR_COMMITS',
}

export type InterviewScore = {
  id?: string;
  scoreType: InterviewScoreEnum;
  score: number;
  technicalChallengeCompetencyScoreId?: string;
  technicalChallengeCompetencyScore?: TechnicalChallengeCompetencyScore;
};

export type SalaryRange = {
  min: number;
  max: number;
};

export enum JobStatus {
  ACTIVE = 'Active',
  ON_HOLD = 'On-hold',
  FILLED = 'Filled',
  CANCELLED = 'Cancelled',
  INACTIVE = 'Inactive',
}

export enum JobStart {
  IMMEDIATELY = 'Immediately',
  WITHIN_1_MONTH = 'Within 1 month',
  WITHIN_3_MONTHS = 'Within 3 months',
}

export type JobNoteTopic = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type JobNote = {
  id?: string;
  job?: Job;
  text: string;
  topic: JobNoteTopic;
  author?: User;
  createdAt: string;
  updatedAt: string;
};

export enum TimeZone {
  EST = 'EST',
  CST = 'CST',
  MST = 'MST',
  PST = 'PST',
  GMT = 'GMT',
  CET = 'CET',
  EET = 'EET',
  MSK = 'MSK',
  JST = 'JST',
}

export type WorkingTime = {
  id?: string;
  start: string;
  end: string;
};

export type Account = {
  id?: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type Benefit = {
  id?: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type Job = {
  account?: Account;
  benefits?: Benefit[];
  candidates?: PersonType[];
  createdAt: string;
  description: string;
  developerType: string;
  experience: string;
  id: string;
  jobLink: string | null;
  notes: JobNote[];
  numberOfDevs: number;
  requirements: string;
  salaryRange: SalaryRange;
  salaryRangeMax: number;
  salaryRangeMin: number;
  status: JobStatus;
  techs: TechStackType[];
  timeZone?: TimeZone;
  timeZoneOverlap?: number;
  title: string;
  topics: JobNoteTopic[];
  updatedAt: string;
  whenToStart: JobStart;
  workingTimes: WorkingTime[];
};

export type APIResponse<T> = Promise<AxiosResponse<T>>;

export type PaginationParams = {
  take: number;
  page: number;
};

export type SortParams = {
  column: string;
  order: string;
};

export type CelebrationType = 'BIRTHDATE' | 'TRIOVERSARY';

type BaseCelebration = {
  celebrationType: CelebrationType;
  date: string;
  name: string;
  personId: string;
  pictureUrl?: string;
  staffMemberId: string;
};

type BirthdayCelebration = BaseCelebration & {
  celebrationType: 'BIRTHDATE';
};

type TrioversaryCelebration = BaseCelebration & {
  celebrationType: 'TRIOVERSARY';
  anniversaryNumber: number;
};

export type Celebration = BirthdayCelebration | TrioversaryCelebration;

export type OpenAiCompletionParams = {
  prompt: string;
  systemMessage: string;
  resumeUrl: string;
};

export type OpenAIPreScreeningReviewParams = {
  questions: {
    questionText: string;
    audioUrl: string;
  }[];
};
